import { toThemeColor } from "../theme";
import { snapLine } from "../overlays/grid";
import { DRAWING_FILL_OPACITY, DRAWING_LINE_CAP_RATIO } from "./drawingConstants";
import { dashSegment } from "./drawingDash";

const IDENTITY_TRANSFORM = [1, 0];
const LINE_ENDPOINT_FLOATS = 4;
const FILL_COLOR_OFFSET = 6;
const ALPHA_CHANNEL = 3;

const colorCache = new Map();

/**
 * @param {string} cssColor - a colour of a drawing
 * @returns {number[]} RGBA in 0..1; only a few colours are in use, so they are kept
 */
const drawingColorRgba = (cssColor) => {
  if (!colorCache.has(cssColor)) colorCache.set(cssColor, toThemeColor(cssColor).rgba);

  return colorCache.get(cssColor);
};

// Lines along an axis cover whole pixels, like the grid; the others are smoothed by the context
const toDeviceSegment = ([x0, y0, x1, y1], pixelRatio, lineWidth) => {
  if (y0 === y1) {
    const y = snapLine(y0, pixelRatio, lineWidth);

    return [x0 * pixelRatio, y, x1 * pixelRatio, y];
  }

  if (x0 === x1) {
    const x = snapLine(x0, pixelRatio, lineWidth);

    return [x, y0 * pixelRatio, x, y1 * pixelRatio];
  }

  return [x0 * pixelRatio, y0 * pixelRatio, x1 * pixelRatio, y1 * pixelRatio];
};

const shownSegments = ({ segments, isDashed, width }, pane) =>
  isDashed ? segments.flatMap((segment) => dashSegment(segment, pane, width)) : segments;

const buildLines = (shapes, pane, pixelRatio, floatsPerLine) => {
  const strokes = shapes
    .flatMap((shape) => shape.strokes)
    .map((stroke) => ({ ...stroke, segments: shownSegments(stroke, pane) }));
  const segmentCount = strokes.reduce((count, { segments }) => count + segments.length, 0);
  const data = new Float32Array(segmentCount * floatsPerLine);
  let offset = 0;

  strokes.forEach(({ segments, color: cssColor, width, opacity = 1 }) => {
    const lineWidth = Math.max(1, Math.round(width * pixelRatio));
    const color = drawingColorRgba(cssColor);

    segments.forEach((segment) => {
      data.set(toDeviceSegment(segment, pixelRatio, lineWidth), offset);
      data.set(color, offset + LINE_ENDPOINT_FLOATS);
      data[offset + LINE_ENDPOINT_FLOATS + ALPHA_CHANNEL] = color[ALPHA_CHANNEL] * opacity;
      data[offset + floatsPerLine - 1] = lineWidth;
      offset += floatsPerLine;
    });
  });

  return data;
};

const buildFills = (shapes, pixelRatio, floatsPerFill) => {
  const fills = shapes.flatMap((shape) => shape.fills);
  const trapCount = fills.reduce((count, { traps }) => count + traps.length, 0);
  const data = new Float32Array(trapCount * floatsPerFill);
  let offset = 0;

  fills.forEach(({ traps, color: cssColor }) => {
    const color = drawingColorRgba(cssColor);

    traps.forEach((trap) => {
      data.set(
        trap.map((value) => Math.round(value * pixelRatio)),
        offset,
      );
      data.set(color, offset + FILL_COLOR_OFFSET);
      data[offset + FILL_COLOR_OFFSET + ALPHA_CHANNEL] = color[ALPHA_CHANNEL] * DRAWING_FILL_OPACITY;
      offset += floatsPerFill;
    });
  });

  return data;
};

/**
 * Drawings on the WebGL layer, in device pixels, inside the clip of the price
 * pane: the fills under their own lines. Rebuilt each frame: a few segments.
 *
 * @param {object} params
 * @param {object} params.fillRenderer
 * @param {object} params.lineRenderer
 * @param {object[]} params.shapes - from resolveShape
 * @param {number[]} params.viewport - [width, height] of the canvas, device px
 * @param {{ width: number, height: number }} params.pane - the price pane, CSS px
 * @param {number} params.pixelRatio
 */
export const drawDrawingGeometry = ({ fillRenderer, lineRenderer, shapes, viewport, pane, pixelRatio }) => {
  const transforms = { viewport, xTransform: IDENTITY_TRANSFORM, yTransform: IDENTITY_TRANSFORM };

  fillRenderer.setInstances(buildFills(shapes, pixelRatio, fillRenderer.floatsPerInstance));
  fillRenderer.draw(transforms);
  lineRenderer.setInstances(buildLines(shapes, pane, pixelRatio, lineRenderer.floatsPerInstance));
  lineRenderer.draw({ ...transforms, capRatio: DRAWING_LINE_CAP_RATIO });
};
